import React from 'react';
import { Link } from 'react-router-dom';
import { LabCategoryKey, LAB_CATEGORIES } from './types';

interface LabHeroV2Props {
  category: LabCategoryKey;
  isZh: boolean;
}

/**
 * LabHeroV2 — Barista Map V2 規格
 *  - 一張大圖 + 左下角單一大字標題
 *  - 標題下方為小尺寸麵包屑：首頁 / 醞玖製研中心 / [當前分類]
 *  - 與 ShopHeroV2 / StoreHeroV2 / WholesaleHeroV2 風格一致
 */
const HERO_IMAGES: Record<LabCategoryKey, string> = {
  all: 'https://images.pexels.com/photos/1695052/pexels-photo-1695052.jpeg?auto=compress&w=1920',
  news: 'https://images.pexels.com/photos/1187766/pexels-photo-1187766.jpeg?auto=compress&w=1920',
  guide: 'https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?auto=compress&w=1920',
  beanstory: 'https://images.pexels.com/photos/4109743/pexels-photo-4109743.jpeg?auto=compress&w=1920',
  events: 'https://images.pexels.com/photos/1638280/pexels-photo-1638280.jpeg?auto=compress&w=1920',
};

const LabHeroV2: React.FC<LabHeroV2Props> = ({ category, isZh }) => {
  const lang = isZh ? 'zh' : 'en';

  const labRootLabel = isZh ? '醞玖製研中心' : 'Lab';
  const homeLabel = isZh ? '首頁' : 'Home';

  const title =
    category === 'all'
      ? labRootLabel
      : isZh
        ? LAB_CATEGORIES[category].zh
        : LAB_CATEGORIES[category].en;

  // 左下角小麵包屑：首頁 / 醞玖製研中心 / [當前分類（如有）]
  const crumbs: { label: string; to?: string }[] = [
    { label: homeLabel, to: `/${lang}` },
    category === 'all'
      ? { label: labRootLabel } // 在分類為 all 時，醞玖製研中心為當前頁
      : { label: labRootLabel, to: `/${lang}/lab` },
  ];

  if (category !== 'all') {
    crumbs.push({
      label: isZh ? LAB_CATEGORIES[category].zh : LAB_CATEGORIES[category].en,
    });
  }

  return (
    <section
      className="relative w-full overflow-hidden bg-black"
      style={{ minHeight: '60vh', height: '60vh' }}
    >
      <img
        src={HERO_IMAGES[category]}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/55 to-transparent" />

      <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-10 lg:px-12 flex flex-col justify-end pb-10 md:pb-14">
        <h1
          className="text-white tracking-wide leading-[1.05]"
          style={{
            fontFamily: '"EB Garamond","Noto Serif TC",serif',
            fontWeight: 400,
            fontSize: 'clamp(2.5rem, 6vw, 5.5rem)',
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </h1>

        {/* 小麵包屑：左下角，圖片內 */}
        <nav
          aria-label="Breadcrumb"
          className="mt-3 md:mt-4 text-white/80"
          style={{
            fontFamily: '"EB Garamond","Noto Serif TC",serif',
            fontWeight: 300,
            fontSize: '12px',
            letterSpacing: '0.08em',
          }}
        >
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <li key={`${c.label}-${i}`} className="flex items-center gap-x-2">
                  {c.to && !isLast ? (
                    <Link
                      to={c.to}
                      className="hover:text-white transition-colors"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span className={isLast ? 'text-white' : ''}>{c.label}</span>
                  )}
                  {!isLast && <span className="text-white/50">/</span>}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </section>
  );
};

export default LabHeroV2;
